// Shared unsafe-input predicates keep compiler and guard policy drift lower.
// They cover blocked URL schemes, CSS imports, and payload breakout patterns.

// Matches blocked request schemes. Example match: "javascript:"
const BLOCKED_SCHEME = /^(?:javascript|vbscript|data|blob|file):/i
// Matches CSS url() values that can load remote or executable resources. Example match: "url(https://x)"
const UNSAFE_CSS_URL = /url\(\s*(['"])?\s*(https?:|\/\/|data:|javascript:|vbscript:)/i
// Matches CSS @import values that can load remote or executable resources. Example match: "@import \"https://x.css\""
const UNSAFE_CSS_IMPORT = /@import\s+(?:url\(\s*)?(?:"|')?\s*(?:https?:|\/\/|data:|javascript:|vbscript:|blob:|file:)/i
// Decodes CSS escape tokens for security matching. Example match: "\\00006a "
const CSS_ESCAPE = /\\([0-9a-fA-F]{1,6}\s?|.)/g
// Matches decoded hex escape bodies. Example match: "00006a"
const CSS_HEX_ESCAPE_BODY = /^[0-9a-fA-F]+$/

// Exposes whether one request starts with a blocked executable or inline scheme.
export function hasBlockedScheme(request: string): boolean {
  return BLOCKED_SCHEME.test(normalizeCssSecurityText(request).trim())
}

// Exposes whether one remote `.luis` fetch URL is safe to request.
export function isSafeLuisFetchUrl(url: string): boolean {
  return !hasBlockedScheme(url)
}

// Exposes whether one raw CSS-like payload contains unsafe breakout content.
export function hasUnsafeCssPayload(input: string): boolean {
  const normalized = normalizeCssSecurityText(input)
  return normalized.includes('</style')
    || normalized.includes('<script')
    || normalized.includes('</script')
    || normalized.includes('javascript:')
    || UNSAFE_CSS_URL.test(input)
    || UNSAFE_CSS_URL.test(normalized)
    || UNSAFE_CSS_IMPORT.test(input)
    || UNSAFE_CSS_IMPORT.test(normalized)
}

// Normalize CSS escapes before checking blocked schemes and resource loads.
function normalizeCssSecurityText(input: string): string {
  return input.replace(CSS_ESCAPE, (_match, escaped: string) => {
    const hex = readCssHexEscape(escaped)
    // Decode hexadecimal CSS escapes like `\6a` into their text form.
    if (hex) {
      const codePoint = Number.parseInt(hex, 16)
      // Ignore invalid Unicode code points so malformed escapes cannot crash guard checks.
      if (codePoint > 0x10ffff) {
        return ''
      } else {
        // Return the decoded CSS escape character.
        return String.fromCodePoint(codePoint)
      }
    } else {
      // Treat simple CSS escapes as the escaped literal character.
      return escaped
    }
  }).toLowerCase()
}

// Return the hex portion of a CSS escape token when present.
function readCssHexEscape(escaped: string): string | null {
  const trimmed = escaped.trim()
  // Keep non-hex one-character escapes on the simple escape path.
  if (!CSS_HEX_ESCAPE_BODY.test(trimmed)) {
    return null
  } else {
    // Return the bounded CSS hex escape payload.
    return trimmed
  }
}
